/**
 * Password recovery page component.
 *
 * Flow (Kratos code-based recovery):
 *   1. User enters email → Kratos sends a 6-digit code (state: sent_email)
 *   2. User enters the code → Kratos validates (state: passed_challenge)
 *   3. Kratos creates a privileged session — we create a settings flow
 *      inline and render the password fields so the user sets a new password
 *      without leaving this page.
 *   4. Password updated → redirect to /login
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { RecoveryFlow, SettingsFlow } from '@ory/client';
import { kratos, getFlowId, handleKratosError } from '../lib/kratos';
import FormField from '../components/FormField';
import FlowMessages from '../components/FlowMessages';
import LoadingSpinner from '../components/LoadingSpinner';
import { LOGO_MARK_LIGHT, LOGO_MARK_DARK, LOGO_ALT } from '../brand';

type Phase = 'email' | 'code' | 'password' | 'done';

export default function Recovery() {
  const location = useLocation();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<RecoveryFlow | null>(null);
  const [settingsFlow, setSettingsFlow] = useState<SettingsFlow | null>(null);
  const [phase, setPhase] = useState<Phase>('email');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryCode, setRecoveryCode] = useState('');

  const initializeFlow = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const flowId = getFlowId(location.search);

      if (flowId) {
        try {
          const { data } = await kratos.getRecoveryFlow({ id: flowId });
          setFlow(data);

          if (data.state === 'sent_email') {
            setPhase('code');
          } else if (data.state === 'passed_challenge') {
            await loadSettingsFlow();
          }
          return;
        } catch {
          // Flow expired or invalid — create a fresh one below
        }
      }

      const { data } = await kratos.createBrowserRecoveryFlow();
      navigate(`/recovery?flow=${data.id}`, { replace: true });
      setFlow(data);
    } catch (err) {
      const msg = handleKratosError(err);
      if (msg.toLowerCase().includes('already logged in') || msg.toLowerCase().includes('session already')) {
        await loadSettingsFlow();
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  }, [location.search, navigate]);

  const loadSettingsFlow = async () => {
    try {
      const { data } = await kratos.createBrowserSettingsFlow();
      setSettingsFlow(data);
      setPhase('password');
    } catch (err) {
      setError(handleKratosError(err));
    }
  };

  useEffect(() => {
    if (phase !== 'email') return;
    initializeFlow();
  }, [initializeFlow]);

  // Step 1: Submit email
  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(event.currentTarget);
    const values: Record<string, string> = {};
    formData.forEach((value, key) => {
      values[key] = value.toString();
    });

    try {
      const { data } = await kratos.updateRecoveryFlow({
        flow: flow!.id,
        updateRecoveryFlowBody: values as unknown as Parameters<typeof kratos.updateRecoveryFlow>[0]['updateRecoveryFlowBody'],
      });

      setFlow(data);

      if (data.state === 'sent_email') {
        setPhase('code');
      }
    } catch (err) {
      const errorMessage = handleKratosError(err);
      if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        initializeFlow();
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Submit recovery code
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow || !recoveryCode) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const csrfNode = flow.ui.nodes.find(
        (n) => (n.attributes as { name?: string }).name === 'csrf_token'
      );
      const csrf = csrfNode ? (csrfNode.attributes as { value?: string }).value as string : '';

      const { data } = await kratos.updateRecoveryFlow({
        flow: flow.id,
        updateRecoveryFlowBody: {
          method: 'code',
          code: recoveryCode,
          csrf_token: csrf,
        } as any,  // eslint-disable-line @typescript-eslint/no-explicit-any
      });

      setFlow(data);

      if (data.state === 'passed_challenge') {
        await loadSettingsFlow();
      }
    } catch (err) {
      const msg = handleKratosError(err);
      if (msg.toLowerCase().includes('already logged in') || msg.toLowerCase().includes('session already')) {
        await loadSettingsFlow();
      } else {
        setError(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 3: Submit new password
  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!settingsFlow) return;
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(event.currentTarget);
    const values: Record<string, string> = {};
    formData.forEach((value, key) => {
      values[key] = value.toString();
    });

    try {
      await kratos.updateSettingsFlow({
        flow: settingsFlow.id,
        updateSettingsFlowBody: {
          method: 'password',
          ...values,
        } as any,  // eslint-disable-line @typescript-eslint/no-explicit-any
      });

      setPhase('done');
    } catch (err: unknown) {
      // Kratos returns 422 "browser location change required" on success
      const resp = (err as { response?: { status?: number } })?.response;
      if (resp?.status === 422) {
        setPhase('done');
        return;
      }
      setError(handleKratosError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (error && !flow && phase === 'email') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">Error</h1>
          <p className="text-content-secondary mb-4">{error}</p>
          <button onClick={initializeFlow} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Done: password changed ──
  if (phase === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-content-primary mb-2">Password Updated</h1>
          <p className="text-content-secondary mb-6">
            Your password has been changed. You can now sign in with your new password.
          </p>
          <Link to="/login" className="btn-primary inline-block">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-md w-full">
        <div className="text-center mb-8">
          <img
            src={LOGO_MARK_LIGHT}
            alt={LOGO_ALT}
            className="h-16 w-16 mx-auto mb-4 dark:hidden"
          />
          <img
            src={LOGO_MARK_DARK}
            alt={LOGO_ALT}
            className="h-16 w-16 mx-auto mb-4 hidden dark:block"
          />
          <h1 className="text-2xl font-bold text-content-primary">
            {phase === 'password' ? 'Set New Password' : 'Reset Password'}
          </h1>
          <p className="text-content-secondary mt-1">
            {phase === 'email' && 'Enter your email to receive a recovery code'}
            {phase === 'code' && 'Enter the 6-digit code we sent to your email'}
            {phase === 'password' && 'Choose a new password for your account'}
          </p>
        </div>

        <FlowMessages messages={phase === 'password' ? settingsFlow?.ui.messages : flow?.ui.messages} />

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {/* ── Phase: email ── */}
        {phase === 'email' && flow && (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            {/* Hidden fields from all groups */}
            {flow.ui.nodes
              .filter((n) => (n.attributes as { type?: string }).type === 'hidden')
              .map((node, i) => (
                <FormField key={`hidden-${i}`} node={node} disabled={isSubmitting} />
              ))}

            {/* Visible input fields from all groups (email is in the code group) */}
            {flow.ui.nodes
              .filter((n) => {
                const type = (n.attributes as { type?: string }).type;
                return type !== 'hidden' && type !== 'submit';
              })
              .map((node, i) => (
                <FormField key={`field-${i}`} node={node} disabled={isSubmitting} />
              ))}

            <input type="hidden" name="method" value="code" />

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full flex items-center justify-center"
            >
              {isSubmitting ? <LoadingSpinner size="sm" /> : 'Send Recovery Code'}
            </button>
          </form>
        )}

        {/* ── Phase: code ── */}
        {phase === 'code' && (
          <>
            <div className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-4 py-3 rounded-lg mb-4 text-sm flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Check your inbox for the recovery code.</span>
            </div>
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="block w-full text-center text-3xl tracking-[0.5em] font-mono px-4 py-4 border border-border rounded-lg bg-surface-800 text-content-primary focus:ring-accent focus:border-accent"
                maxLength={6}
                disabled={isSubmitting}
                autoFocus
              />
              <button
                type="submit"
                disabled={isSubmitting || recoveryCode.length !== 6}
                className="btn-primary w-full flex items-center justify-center"
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Verify Code'}
              </button>
            </form>
            <div className="mt-4 text-center">
              <button
                onClick={() => { setError(null); setRecoveryCode(''); setFlow(null); setPhase('email'); initializeFlow(); }}
                className="text-sm text-content-tertiary hover:text-content-secondary"
              >
                Try a different email
              </button>
            </div>
          </>
        )}

        {/* ── Phase: password ── */}
        {phase === 'password' && settingsFlow && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {settingsFlow.ui.nodes
              .filter((n) => n.group === 'default' && (n.attributes as { type?: string }).type === 'hidden')
              .map((node, i) => (
                <FormField key={`hidden-${i}`} node={node} disabled={isSubmitting} />
              ))}

            {settingsFlow.ui.nodes
              .filter((n) => n.group === 'password' && (n.attributes as { type?: string }).type !== 'submit')
              .map((node, i) => (
                <FormField key={`pw-${i}`} node={node} disabled={isSubmitting} />
              ))}

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full flex items-center justify-center"
            >
              {isSubmitting ? <LoadingSpinner size="sm" /> : 'Set New Password'}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 font-medium"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
